import Dropdown from '@jetbrains/ring-ui/components/dropdown/dropdown'
import type {ListDataItem} from '@jetbrains/ring-ui/components/list/consts'
import {Type as ListItemType} from '@jetbrains/ring-ui/components/list/consts'
import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import {Directions} from '@jetbrains/ring-ui/components/popup/popup.consts'
import PopupMenu from '@jetbrains/ring-ui/components/popup-menu/popup-menu'
import Tag from '@jetbrains/ring-ui/components/tag/tag'
import {memo, Suspense, useCallback, useMemo} from 'react'

import {useAppSelector} from '../../../../hooks/react-redux'
import {useIsPipelinesSelected} from '../../../../hooks/routes'
import {useSetUserProperty} from '../../../../hooks/useSetUserProperty'
import {getServerInfo} from '../../../../selectors'
import {internalProps} from '../../../../types/BS_types'
import cssVariables from '../../../../utils/cssVariables'
import {truthy} from '../../../../utils/guards'
import {resolveHelpURL, resolveRelative} from '../../../../utils/url'
import {UserProperties} from '../../../../utils/userProperties'
import {useIsHintsModeEnabled} from '../../../packages/Hints/Hints.hooks'
import {TRY_PIPELINES_LINK} from '../../../pages/PipelinesPages/components/PipelinesPromoBanner/PipelinesPromoPopup/PipelinesPromoPopup.const'
import {
  isPipelinesEnabled,
  isPipelinesEnabledInExclusiveMode,
  isPipelinesPromoBannerEnabled,
} from '../../../pages/PipelinesPages/utils/featureToggles'
import styles from '../Header.css'
import LinkHost from '../Links/LinkHost/LinkHost'

import Footer from './Footer/Footer.container'
import {feedbackHref, whatsNewHref} from './HeaderTrayHelp.consts'

const PopupDirections = [Directions.RIGHT_TOP]

const supportBaseUrl = 'https://teamcity-support.jetbrains.com/hc/en-us/requests/new'

type SupportUrlProps = {
  licenseIsCloud: boolean | undefined
  licenseIsEnterpise: boolean | undefined
  version: string | undefined
  type?: 'teamcity_pipelines' | 'teamcity_cloud'
}

function getSupportUrl({
  licenseIsCloud,
  licenseIsEnterpise,
  version,
  type = 'teamcity_cloud',
}: SupportUrlProps): string {
  const params = new URLSearchParams()
  if (licenseIsCloud) {
    params.set('ticket_form_id', '360003300299')
    params.set(
      type === 'teamcity_cloud' ? 'teamcity_cloud_url' : 'teamcity_pipelines_url',
      window.location.hostname,
    )
    params.set('product_type', type)
  } else {
    params.set('ticket_form_id', '66621')
    if (licenseIsEnterpise) {
      params.set('license_type', 'enterprise')
    }
    if (version != null && version.length > 0) {
      const shortVersion = version.split(' ').find(i => i.length > 0)
      if (shortVersion != null) {
        params.set('version', shortVersion)
      }
    }
  }
  return `${supportBaseUrl}?${params.toString()}`
}

function useHelpDropdownContent(): ReadonlyArray<ListDataItem> {
  const isPipelinesSelected = useIsPipelinesSelected()
  const isPipelines =
    isPipelinesEnabledInExclusiveMode() || (isPipelinesEnabled && isPipelinesSelected)
  const {isEnabled, isGuestOrRoot} = useIsHintsModeEnabled()
  const setUserProperty = useSetUserProperty()
  const toggleTipsMode = useCallback(() => {
    setUserProperty(UserProperties.TIPS_MODE, isEnabled ? 'false' : 'true')
  }, [setUserProperty, isEnabled])

  const {licenseIsCloud, licenseIsEnterpise, version} = useAppSelector(getServerInfo) || {}
  const showLicenseAgreement = internalProps['teamcity.ui.showLicenseAgreementToAllUsers'] !== false

  return useMemo(() => {
    if (isPipelines) {
      return [
        {
          label: 'Documentation',
          href: 'https://www.jetbrains.com/help/teamcity/pipelines/teamcity-pipelines.html',
          rgItemType: ListItemType.LINK,
          target: '_blank',
          rel: 'noreferrer',
        },
        {
          label: 'Submit support request',
          href: getSupportUrl({
            licenseIsCloud,
            licenseIsEnterpise,
            version,
            type: 'teamcity_pipelines',
          }),
          rgItemType: ListItemType.LINK,
          target: '_blank',
          rel: 'noreferrer',
        },
      ]
    }

    return [
      {
        label: 'Documentation',
        href: resolveHelpURL('?TeamCity Documentation'),
        rgItemType: ListItemType.LINK,
        target: '_blank',
        rel: 'noreferrer',
      },
      {
        label: 'TeamCity Kotlin DSL',
        href: resolveRelative('/app/dsl-documentation/index.html'),
        rgItemType: ListItemType.LINK,
        target: '_blank',
        rel: 'noreferrer',
      },
      {
        label: 'Getting started',
        href: resolveHelpURL('?Configure and Run Your First Build'),
        rgItemType: ListItemType.LINK,
        target: '_blank',
        rel: 'noreferrer',
      },
      !isGuestOrRoot && {
        key: 'enable-tips',
        label: `${isEnabled ? 'Hide' : 'Show'} hints`,
        onClick: toggleTipsMode,
      },
      showLicenseAgreement && {
        label: 'License Agreement',
        href: resolveRelative('/showAgreement.html'),
        rgItemType: ListItemType.LINK,
        target: '_blank',
        rel: 'noreferrer',
      },
      {
        rgItemType: ListItemType.SEPARATOR,
      },
      isPipelinesPromoBannerEnabled && {
        key: 'try-pipelines',
        className: styles.tryPipelinesLink,
        href: TRY_PIPELINES_LINK,
        children: (
          <div className={styles.tryPipelinesLink}>
            {'Try Pipelines'}
            <div className={styles.tryPipelinesNewTagWrapper}>
              <Tag
                textColor={cssVariables['--ring-success-color']}
                backgroundColor={cssVariables['--ring-added-background-color']}
                readOnly
              >
                {'New'}
              </Tag>
            </div>
          </div>
        ),
        target: '_blank',
        rel: 'noreferrer',
      },
      {
        label: 'Share feedback',
        href: feedbackHref,
        rgItemType: ListItemType.LINK,
        target: '_blank',
        rel: 'noreferrer',
      },
      (licenseIsCloud || licenseIsEnterpise) && {
        label: 'Submit support request',
        href: getSupportUrl({licenseIsCloud, licenseIsEnterpise, version}),
        rgItemType: ListItemType.LINK,
        target: '_blank',
        rel: 'noreferrer',
      },
      {
        key: 'whats-new',
        href: whatsNewHref,
        children: `What's new`,
        target: '_blank',
        rel: 'noreferrer',
      },
    ].filter(truthy)
  }, [
    isPipelines,
    licenseIsCloud,
    licenseIsEnterpise,
    version,
    isGuestOrRoot,
    isEnabled,
    toggleTipsMode,
    showLicenseAgreement,
  ])
}

function HeaderTrayHelp() {
  const popupMenuItems = useHelpDropdownContent()

  return (
    <Dropdown data-test={'help-dropdown'} anchor={<LinkHost title="Help" icon="help-20px" />}>
      <PopupMenu
        className={styles.popup}
        directions={PopupDirections}
        data={popupMenuItems}
        closeOnSelect
        offset={20}
        top={-2}
        hint={
          <Suspense fallback={<LoaderInline>{'Loading...'}</LoaderInline>}>
            <Footer />
          </Suspense>
        }
      />
    </Dropdown>
  )
}

export default memo(HeaderTrayHelp)
